import React from "react";

import { blogApi } from "../api/blogApi";
import { podcastsApi } from "../api/podcastsApi";
import { psychologistTestsApi } from "../api/psychologistTestsApi";
import { sessionsApi } from "../api/sessionsApi";
import { useToast } from "../ui/toast/ToastProvider";
import { extractTotal } from "./subscriptionLimits";
import { useSubscriptionLimitsGate } from "./useSubscriptionLimitsGate";

function pickListItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.content)) return payload.content;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function isConferenceShadowSession(row) {
  const source = row && typeof row === "object" ? row : {};
  const typeToken = String(
    source?.kind ??
      source?.sessionKind ??
      source?.session_kind ??
      source?.bookingType ??
      source?.booking_type ??
      source?.type ??
      ""
  ).toUpperCase();
  if (typeToken.includes("CONFERENCE") || typeToken.includes("DIRECT")) return true;

  const capacity = Number(source?.capacityClients ?? source?.capacity_clients ?? source?.capacity ?? NaN);
  const minCapacity = Number(source?.minCapacity ?? source?.min_capacity ?? NaN);
  const price = Number(source?.priceAtTime ?? source?.price_at_time ?? source?.price ?? NaN);
  return (
    Number.isFinite(capacity) &&
    capacity === 1 &&
    Number.isFinite(minCapacity) &&
    minCapacity === 1 &&
    (!Number.isFinite(price) || price <= 0)
  );
}

const FEATURE_LOADERS = {
  blogPosts: async () => {
    const payload = await blogApi.listMine({ page: 0, size: 1 });
    return extractTotal(payload, 0);
  },
  podcasts: async () => {
    const payload = await podcastsApi.getMyPodcasts({ page: 0, size: 1 });
    return extractTotal(payload, 0);
  },
  tests: async () => {
    const payload = await psychologistTestsApi.listMine({ page: 0, size: 1 });
    return extractTotal(payload, 0);
  },
  groupSessions: async () => {
    const payload = await sessionsApi.listMyGroupSessions({ page: 0, size: 200 });
    const items = pickListItems(payload);
    return items.filter((row) => !isConferenceShadowSession(row)).length;
  },
};

function normalizePath(input = "") {
  const raw = String(input || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      return `${parsed.pathname || ""}${parsed.search || ""}`;
    } catch {
      return raw;
    }
  }
  return raw;
}

export function featureKeyForCreatePath(input = "") {
  const path = normalizePath(input);
  if (!path) return "";

  if (path.startsWith("/dashboard/blog/new")) return "blogPosts";
  if (path.startsWith("/dashboard/podcasts/new")) return "podcasts";
  if (path.startsWith("/dashboard/tests/new")) return "tests";
  if (path.startsWith("/dashboard/group-sessions/new")) return "groupSessions";
  if (path.startsWith("/dashboard/group-sessions/create")) return "groupSessions";

  return "";
}

export function useCreateSectionGate() {
  const toast = useToast();
  const { ensureWithinLimit, limitModal, currentPlanCode, refreshSubscriptionLimits } = useSubscriptionLimitsGate();

  const checkCreateAccess = React.useCallback(
    async (targetPath) => {
      const featureKey = featureKeyForCreatePath(targetPath);
      if (!featureKey) return true;

      const loadUsage = FEATURE_LOADERS[featureKey];
      if (typeof loadUsage !== "function") return true;

      try {
        const usage = await loadUsage();
        return ensureWithinLimit({ featureKey, usage });
      } catch (error) {
        toast.error(error?.message || "Не удалось проверить лимит подписки");
        return false;
      }
    },
    [ensureWithinLimit, toast]
  );

  return {
    checkCreateAccess,
    limitModal,
    ensureWithinLimit,
    currentPlanCode,
    refreshSubscriptionLimits,
  };
}
